import type { Messages } from './fr';

const pt: Messages = {
  app: { tagline: 'O jogo de letras que fala várias línguas.' },
  nav: {
    back: 'Voltar',
    home: 'Início',
    rules: 'Regras',
    settings: 'Definições',
    about: 'Sobre',
    account: 'Conta',
    leaderboards: 'Classificações',
    tournaments: 'Torneios',
    daily: 'Desafio do dia',
    admin: 'Administração',
  },
  common: {
    cancel: 'Cancelar',
    confirm: 'Confirmar',
    close: 'Fechar',
    save: 'Guardar',
    loading: 'A carregar…',
    yes: 'Sim',
    no: 'Não',
    copy: 'Copiar',
    copied: 'Copiado!',
    retry: 'Tentar novamente',
  },
  langNames: {
    fr: 'francês',
    en: 'inglês',
    es: 'espanhol',
    de: 'alemão',
    it: 'italiano',
    pt: 'português',
  },
  home: {
    quickPlay: 'Jogar',
    quickPlayDesc: 'Partida Clássica de 2 minutos · {{lang}}',
    modesTitle: 'Modos de jogo',
    newGame: 'Nova partida',
    online: 'Jogar online',
  },
  modes: {
    classic: {
      name: 'Clássico',
      short: 'Uma língua · 2 min',
      desc: 'Uma só língua, igual para todos. Faça o máximo de pontos em 120 segundos. Sem bónus de língua nem por terminar mais cedo.',
    },
    polyglot: {
      name: 'Poliglota comum',
      short: '2 ou 3 línguas para todos · 2 min',
      desc: 'As mesmas 2 ou 3 línguas para todos. Bónus de diversidade (+20 % com duas línguas, +30 % com três) se encontrar pelo menos 3 palavras em cada língua.',
    },
    duel: {
      name: 'Duelo tático',
      short: '10 palavras ou 2 min · contrato',
      desc: 'Cada jogador escolhe em segredo 1, 2 ou 3 línguas. Objetivo: 10 palavras válidas. Contrato linguístico + bónus de velocidade se chegar às 10 palavras antes do fim. A 10.ª palavra termina a sua ronda.',
    },
    sprint: {
      name: 'Sprint livre',
      short: '1 a 3 línguas · 2 min · contrato',
      desc: 'Escolha de 1 a 3 línguas e faça o máximo de pontos em 120 segundos com contrato linguístico. Sem bónus por terminar mais cedo.',
    },
    relax: {
      name: 'Descontraído',
      short: 'Sem cronómetro',
      desc: 'Sem limite de tempo e sem classificação. As dicas de validade só podem ser ativadas neste modo.',
    },
  },
  setup: {
    title: 'Nova partida',
    mode: 'Modo',
    languagesShared: 'Línguas da partida ({{min}} a {{max}})',
    languagesIndividual: 'As suas línguas (1 a 3, bloqueadas antes do sorteio)',
    errors: {
      duplicate_language: 'Cada língua só pode ser escolhida uma vez.',
      too_few_languages: 'Escolha pelo menos {{min}} língua(s).',
      too_many_languages: 'No máximo {{max}} língua(s).',
    },
    noLanguageBonus: 'Sem bónus de língua neste modo.',
    opponent: 'Adversário',
    noOpponent: 'Nenhum (solo)',
    robotNotice:
      'Os robôs são programas de treino claramente identificados. Nunca contam nas classificações.',
    robotSameLanguages: 'O robô joga com as minhas línguas',
    robotLanguages: 'Línguas do robô',
    loading: 'A preparar o sorteio…',
    start: 'Começar',
    noDraw:
      'Não foi encontrado um sorteio equilibrado para esta combinação. Experimente outras línguas ou o contrato Especialista.',
  },
  contracts: {
    specialist: { name: 'Especialista', desc: 'uma língua, bónus garantido sobre os pontos base.' },
    duo: {
      name: 'Duo',
      desc: 'duas línguas, bónus só com pelo menos 3 palavras distintas em cada uma.',
    },
    trio: {
      name: 'Trio',
      desc: 'três línguas, bónus só com pelo menos 3 palavras distintas em cada uma.',
    },
    diversity: {
      name: 'Diversidade',
      desc: 'bónus de {{pct}} % para todos com pelo menos 3 palavras por língua.',
    },
    short: { specialist: 'Especialista', duo: 'Duo', trio: 'Trio', diversity: 'Diversidade' },
  },
  bots: {
    rookie: 'Iniciante',
    casual: 'Intermédio',
    skilled: 'Avançado',
    expert: 'Especialista',
    rookieDesc: 'Palavras muito comuns, ritmo lento, prefere palavras curtas.',
    casualDesc: 'Palavras comuns, ritmo moderado.',
    skilledDesc: 'Vocabulário comum alargado, ritmo constante.',
    expertDesc: 'Conhece também palavras raras, rápido, aposta em palavras longas.',
    robotName: 'Robô {{level}}',
  },
  game: {
    timeLeft: 'Tempo restante',
    getReady: 'Preparar…',
    untimed: '∞',
    score: 'Pontuação',
    points: 'pts',
    wordsOfTarget: '{{n}} / {{target}} palavras',
    wordsCount_one: '{{count}} palavra',
    wordsCount_other: '{{count}} palavras',
    languageCounters: 'Palavras por língua',
    robotNotice: 'Adversário robô (programa de treino)',
    opponentDone: 'terminou',
    finalWarning:
      'Atenção: a sua próxima palavra válida será a 10.ª e terminará a ronda. O seu contrato ainda não está cumprido.',
    currentWord: 'Palavra atual',
    tapLetters: 'Toque nas letras',
    hintValid: 'Válida: {{langs}}',
    hintInvalid: '(Ainda) não é uma palavra',
    attributeTo: 'Atribuir a {{lang}}',
    attribution: 'Língua:',
    auto: 'Auto',
    autoExplain:
      'Auto: a palavra vai para a língua válida que mais precisa de palavras para o seu contrato; caso contrário, segue a ordem das suas línguas.',
    rack: 'Letras do sorteio',
    tileLabel: 'Letra {{letter}}, posição {{pos}}',
    shuffle: 'Baralhar',
    delete: 'Apagar',
    clear: 'Limpar',
    submit: 'Validar',
    foundWords: 'Palavras encontradas',
    bonusSecured: 'bónus de língua garantido',
    finish: 'Terminar',
    stop: 'Parar',
    stopTitle: 'Parar a ronda?',
    stopBody: 'As suas palavras são mantidas. Parar mais cedo não dá qualquer bónus.',
    finishBody: 'Terminar a partida descontraída e ver o resumo?',
    confirmFinalTitle: 'Última palavra: contrato não cumprido',
    confirmFinalOk: 'Validar mesmo assim',
    confirmFinalBody:
      'Esta 10.ª palavra termina a sua ronda de imediato. O contrato linguístico não será cumprido: sem bónus de língua (o bónus de velocidade mantém-se).',
    accepted: '«{{word}}» +{{points}} ({{lang}})',
  },
  reasons: {
    round_over: 'A ronda terminou.',
    invalid_characters: 'Caracteres não jogáveis.',
    too_short: 'Mínimo de três letras.',
    too_long: 'Palavra demasiado longa.',
    not_formable: 'Impossível com estas letras.',
    duplicate: 'Já encontrada.',
    language_not_allowed: 'Língua não permitida nesta ronda.',
    excluded_word: 'Palavra excluída pela política de conteúdos do jogo.',
    not_a_word: 'Palavra desconhecida nas suas línguas.',
    wrong_language: 'Esta palavra é válida em: {{langs}}.',
    confirmation_required: 'É necessária confirmação.',
    rate_limited: 'Demasiadas tentativas, abrande.',
    not_started: 'A ronda ainda não começou.',
  },
  results: {
    points: 'pontos',
    pts: 'pts',
    base: 'Pontos base',
    fulfilled: 'cumprido',
    missed: 'falhado',
    speedBonus: 'Bónus de velocidade',
    officialTime: 'Tempo oficial',
    target: 'Objetivo',
    endReason: {
      target_reached: 'Objetivo alcançado',
      time_up: 'Tempo esgotado',
      stopped: 'Paragem voluntária',
      abandoned: 'Abandono',
    },
    explainBase: 'As suas palavras valem {{base}} pontos base (10 × (letras − 2)).',
    explainSpecialist: 'Contrato Especialista: +10 % garantido.',
    explainQuotaOk: 'Pelo menos {{quota}} palavras em cada língua: bónus de {{pct}} % obtido.',
    explainQuotaKo:
      'Eram precisas pelo menos {{quota}} palavras em cada língua: sem bónus de língua ({{pct}} % possível).',
    explainSpeedOk: '10 palavras alcançadas em {{time}}: bónus de velocidade de {{pct}} %.',
    explainSpeedKo: 'Objetivo de 10 palavras não alcançado a tempo: sem bónus de velocidade.',
    explainFormula:
      'Total = base × (1 + bónus de língua + bónus de velocidade), arredondado uma única vez à centésima.',
    win: 'Vitória!',
    lose: 'Derrota',
    tie: 'Empate',
    tieExplain: 'Pontuações exatamente iguais: o encontro é declarado empate.',
    you: 'Você',
    robotTitle: '{{name}} (robô)',
    missedWords: 'Palavras comuns que podia formar',
    missedWordsNote:
      'Lista indicativa de palavras frequentes nas suas línguas, para a próxima vez.',
    shareText: '{{mode}} ({{langs}}): {{score}} pontos, {{words}} palavras. Quem faz melhor?',
    playAgain: 'Jogar outra vez',
    share: 'Partilhar resultado',
    changeSetup: 'Mudar de modo',
    localNotice: 'Partida local: não entra em nenhuma classificação.',
    savedToHistory: 'Guardada em «As minhas partidas» (treino).',
    drawAudit: 'Sorteio {{seed}} ({{gen}})',
  },
  errors: {
    network: 'Ligação perdida. A tentar de novo…',
    lexiconLoad: 'Não foi possível carregar o dicionário. Verifique a ligação e tente novamente.',
    notFound: 'Página não encontrada',
    generic: 'Ocorreu um erro.',
  },
  online: {
    title: 'Online',
    challengeFriend: 'Desafiar um amigo',
    challengeFriendDesc: 'Ligação de convite, cada um joga quando quiser',
    liveDuel: 'Duelo ao vivo',
    liveDuelDesc: 'Sala, convite, partida simultânea',
    myGames: 'As minhas partidas',
    offlineNote: 'Servidor inacessível: só o modo solo e o treino estão disponíveis.',
    guestBanner:
      'Está a jogar como convidado. Crie uma conta para guardar o histórico e entrar nas classificações.',
    saveAccount: 'Guardar a minha conta',
  },
  challenge: {
    newTitle: 'Desafiar um amigo',
    returnMatch: 'Ida e volta (2 rondas, sorteios cruzados, sem classificação)',
    create: 'Criar desafio',
    created: 'Desafio criado! Envie esta ligação:',
    share: 'Partilhar a ligação',
    shareText: 'Desafio-te ({{mode}})!',
    expires: 'Expira a {{date}}',
    playMine: 'Jogar a minha ronda agora',
    inviteFrom: '{{name}} desafia-o!',
    acceptTitle: 'Convite',
    chooseLanguages: 'Escolha as suas línguas (bloqueadas após confirmar)',
    compatibleOnly: 'Só são propostas as combinações compatíveis com o sorteio deste desafio.',
    accept: 'Aceitar o desafio',
    expired: 'Este desafio expirou.',
    taken: 'Este desafio já foi aceite por outra pessoa.',
    own: 'É o seu próprio desafio: partilhe a ligação com um amigo.',
    notFound: 'Desafio não encontrado.',
    returnInfo:
      'Ida e volta: duas rondas em dois sorteios diferentes; nenhum resultado é revelado antes do fim.',
    goToMatch: 'Ir para a partida',
  },
  match: {
    readyTitle: 'A sua ronda',
    readyBody:
      'O cronómetro começa assim que as letras aparecem. Fechar ou recarregar a página não repõe o tempo.',
    start: 'Começar a minha ronda',
    waitingOpponent:
      'À espera do adversário. As palavras e a pontuação dele ficam ocultas até ao fim.',
    opponentProgress: '{{name}}: {{count}} palavra(s)',
    leg: 'Ronda {{n}} / 2',
    nextLeg: 'Jogar a ronda seguinte',
    encounterTotal: 'Total do encontro',
    unranked: 'Partida sem classificação',
    ranked: 'Partida classificada',
    reportWord: 'Assinalar uma palavra recusada',
    reportPrompt: 'Palavra a assinalar (e língua)',
    reportSent: 'Obrigado, assinalamento enviado.',
    loadError: 'Não foi possível carregar a partida.',
    opponentNotPlayed: 'O adversário não jogou.',
    opponent: 'Adversário',
  },
  account: {
    title: 'A minha conta',
    guest: 'Conta de convidado',
    registered: 'Conta guardada',
    register: 'Criar uma conta',
    login: 'Entrar',
    logout: 'Sair',
    username: 'Utilizador (3–20 caracteres)',
    password: 'Palavra-passe (mín. 10 caracteres)',
    email: 'E-mail (opcional)',
    displayName: 'Nome visível',
    saveHint: 'O histórico de convidado é mantido quando cria a conta.',
    history: 'Histórico',
    export: 'Exportar os meus dados',
    delete: 'Eliminar a minha conta',
    deleteConfirm: 'Eliminar definitivamente a sua conta? Os seus dados pessoais serão apagados.',
    notifications: 'Lembretes',
    remindersOptIn: 'Receber lembretes (no máximo um por dia, nunca nas horas de silêncio)',
    quietHours: 'Horas de silêncio (das … às …)',
    haveAccount: 'Já tem conta?',
    noAccount: 'Ainda não tem conta?',
    saved: 'Guardado.',
    errors: {
      invalid_credentials: 'Utilizador ou palavra-passe incorretos.',
      username_or_email_taken: 'Utilizador ou e-mail já em uso.',
      nickname_refused: 'Este nome não é permitido.',
      nickname_reserved: 'Este nome está reservado.',
      nickname_length: 'Entre 3 e 20 caracteres.',
      nickname_characters: 'Caracteres não permitidos.',
      invalid_request: 'Verifique os campos.',
      account_suspended: 'Conta suspensa.',
      generic: 'Ocorreu um erro.',
    },
  },
  live: {
    searching: 'À procura de um adversário…',
    searchingBody: 'Espera: {{seconds}} s',
    noFakePlayers: 'Aqui só são propostos jogadores reais; nenhum robô é apresentado como humano.',
    fallbackTitle: 'Ainda ninguém. Pode:',
    fallbackRobot: 'Jogar contra um robô (treino, sem classificação)',
    fallbackAsync: 'Criar um desafio assíncrono para partilhar',
    othersSearching_zero: 'Nenhum outro jogador procura este tipo de partida neste momento.',
    othersSearching_one: '{{count}} outro jogador procura este tipo de partida neste momento.',
    othersSearching_other:
      '{{count}} outros jogadores procuram este tipo de partida neste momento.',
    noOpponentFound: 'Ninguém está disponível por agora. Pode:',
    searchAgain: 'Procurar de novo',
    languagesSecret: 'As suas línguas (secretas, bloqueadas antes do sorteio)',
    quick: 'Partida rápida',
    createRoom: 'Criar uma sala e convidar',
    rankedNote:
      'As partidas entre contas guardadas contam para a classificação (oficial ou experimental conforme o modo).',
    roomFull: 'Esta sala está cheia.',
    roomNotFound: 'Sala não encontrada ou expirada.',
    offline: 'desligado',
    online: 'ligado',
    opponentReady: 'pronto',
    waitingOpponent: 'À espera de um adversário…',
    shareRoom: 'Convide um amigo com esta ligação:',
    sharedReady: 'Línguas definidas para os dois jogadores.',
    lock: 'Estou pronto',
    waitingLock: 'À espera da escolha do adversário…',
    noDrawForBoth: 'Não há sorteio equilibrado para as duas configurações. Escolha outras línguas.',
    disconnectRule:
      'Regra de desconexão: o relógio do servidor continua; volte a esta página para retomar a sua ronda onde está.',
    opponentLeft: '{{name}} desligou-se. O cronómetro continua; pode voltar antes do fim da ronda.',
    opponentBack: '{{name}} voltou.',
    reconnecting: 'Ligação interrompida, a religar… As palavras já validadas mantêm-se.',
    rematch: 'Desforra',
    joinRematch: 'Entrar na desforra',
    reactionsTitle: 'Reações',
    reactions: {
      gg: 'Bem jogado!',
      bravo: 'Bravo!',
      wow: 'Impressionante!',
      oops: 'Ups!',
      thanks: 'Obrigado!',
      good_luck: 'Boa sorte!',
    },
  },
  boards: {
    title: 'Classificações',
    official: 'Oficial',
    experimental: 'Experimental',
    officialDesc: 'Modos simétricos (Clássico, Poliglota comum): mesmas línguas para todos.',
    experimentalDesc:
      'Duelo tático e Sprint livre: classificação à parte, não comparável com os modos simétricos.',
    myRank: 'A sua posição: {{rank}} · {{rating}} Elo · {{games}} partidas',
    empty: 'Ainda sem resultados.',
    player: 'Jogador',
    rating: 'Elo',
    games: 'Partidas',
    rules:
      'Só contam as partidas entre contas guardadas. Robôs, partidas locais e encontros de ida e volta nunca entram na classificação.',
  },
  tournaments: {
    title: 'Torneios',
    intro:
      'Torneios semanais assíncronos: várias rondas, uma tentativa por ronda, as mesmas letras para todos.',
    none: 'Nenhum torneio de momento.',
    weeklyTitle: 'Semanal {{mode}} {{langs}} · {{period}}',
    rounds: '{{n}} ronda(s)',
    entrants: '{{n}} inscrito(s)',
    registered: 'inscrito',
    status: {
      scheduled: 'Em breve',
      open: 'A decorrer',
      closing: 'Fecho',
      closed: 'Terminado',
      cancelled: 'Cancelado',
    },
    frozen: 'Regras {{rules}} e dicionários {{lexicons}} fixados para todo o torneio.',
    attempts: '{{n}} tentativa(s) por ronda.',
    sharingLimit:
      'Limite conhecido: numa competição assíncrona pública, os jogadores podem partilhar respostas. O jogo não pretende impedi-lo por completo.',
    register: 'Inscrever-me',
    accountRequired: 'É necessária uma conta guardada para jogar torneios.',
    myRounds: 'As minhas rondas',
    round: 'Ronda {{n}}',
    resume: 'Retomar',
    play: 'Jogar',
    standings: 'Classificação do torneio',
    roundsPlayed: '{{n}} ronda(s)',
    tieRule: 'Empate exato: mesma posição, sem desempate oculto.',
    backToTournament: 'Voltar ao torneio',
    finalStandings: 'Classificação final',
    myResult: 'O seu total: {{total}} pontos · lugar {{rank}} de {{n}}',
    noRoundYet: 'Ainda não jogou nenhuma ronda.',
    startsAt: 'Início: {{date}}',
    abandoned: 'Não terminada',
    retry: 'Nova tentativa ({{n}} restante(s))',
    closingNote:
      'Encerramento em curso: as rondas já iniciadas podem terminar; a classificação final segue dentro de minutos.',
    errors: {
      competition_not_found: 'Torneio não encontrado.',
      invitation_required: 'Este torneio é privado: é necessário um convite.',
      attempts_exhausted: 'Tentativa já usada nesta ronda.',
      competition_closed: 'O torneio terminou.',
      competition_not_started: 'O torneio ainda não começou.',
      not_registered: 'Inscreva-se primeiro.',
      account_required: 'É necessária uma conta guardada.',
    },
  },
  daily: {
    title: 'Desafio do dia',
    intro: 'Um sorteio por categoria e por dia (UTC), igual para todos. Uma só tentativa.',
    back: 'Voltar aos desafios do dia',
    play: 'Jogar',
    inProgress: 'Em curso',
    resets: 'Novo desafio a {{time}}.',
  },
  profile: {
    title: 'O meu perfil',
    streak: 'dias seguidos',
    bestStreak: 'melhor série',
    rounds: 'partidas online',
    distinctWords: 'palavras diferentes',
    streakRules:
      'A série ganha um dia quando terminas uma partida online com pelo menos uma palavra válida (desafio do dia, desafio entre amigos, duelo ao vivo ou torneio), no máximo uma vez por dia. Dias de tolerância gratuitos restantes: {{grace}} (renovados a cada 30 dias). Fuso horário: {{tz}}.',
    useMyTimezone: 'Usar o meu fuso ({{tz}})',
    tzTooSoon: 'O fuso horário só pode ser alterado uma vez a cada 7 dias.',
    badges: 'Emblemas',
    noBadges: 'Ainda sem emblemas.',
    badgesNote: 'Os emblemas baseiam-se apenas em eventos verificados pelo servidor.',
    cosmetics: 'Personalização',
    cosmeticsNote: 'Puramente visual: sem efeito na pontuação nem nos sorteios.',
    plusOnly: 'Só Plus',
    earnIt: 'Desbloqueia-se a jogar',
    slot: {
      theme: 'Tema',
      avatar: 'Avatar',
      frame: 'Moldura',
    },
    history: 'As minhas partidas',
    gamesSummary: '{{online}} online · {{practice}} treino(s)',
    noGames: 'Ainda não há partidas guardadas.',
    pendingSync: '{{n}} treino(s) à espera de envio (offline).',
    gamesNote:
      'Os treinos (solo, robôs, descontraído) são jogados no teu dispositivo e guardados aqui. O servidor verifica as palavras e recalcula a pontuação, mas não pode verificar o tempo: não contam para a série, os emblemas nem as classificações.',
    sourceOnline: 'Online',
    sourcePractice: 'Treino',
    waitingOpponent: 'resultado depois do adversário',
    playDaily: 'Jogar o desafio do dia',
  },
  badges: {
    first_round: 'Primeira ronda',
    nine_letters: 'Palavra de 9 letras',
    trio_contract: 'Contrato Trio cumprido',
    speed_60: '10 palavras em menos de 60 s',
    words_100: '100 palavras encontradas',
    words_1000: '1000 palavras encontradas',
    streak_7: 'Série de 7 dias',
    streak_30: 'Série de 30 dias',
    first_duel_win: 'Primeira vitória em duelo',
    tournament_participant: 'Participante de torneio',
    tournament_winner: 'Vencedor de torneio',
    tournament_podium: 'Pódio de torneio',
    tournament_top10: 'Top 10 de torneio',
    daily_participant: 'Desafio do dia',
    team_weekly: 'Objetivo de equipa alcançado',
  },
  cosmetics: {
    theme_default: 'Clássico',
    theme_ocean: 'Oceano',
    theme_aurora: 'Aurora',
    theme_sakura: 'Sakura',
    theme_mono: 'Monocromático',
    avatar_letter: 'Inicial',
    avatar_owl: 'Coruja',
    avatar_fox: 'Raposa',
    frame_none: 'Nenhuma',
    frame_team: 'Equipa',
    frame_champion: 'Campeão',
  },
  reminders: {
    title: 'Lembretes',
    explain:
      'Opcionais: no máximo um por dia, nunca nas suas horas de silêncio e nenhum se já jogou. Pode desativá-los a qualquer momento.',
    unavailable: 'Os lembretes não estão disponíveis neste servidor ou dispositivo.',
    quietStart: 'Horas de silêncio: início',
    quietEnd: 'Horas de silêncio: fim',
    enable: 'Ativar lembretes',
    disable: 'Desativar',
    enabled: 'Lembretes ativados.',
    disabled: 'Lembretes desativados.',
    denied: 'Permissão recusada pelo navegador.',
  },
  journal: {
    title: 'Caderno de palavras',
    intro: 'As palavras que encontrou online, por língua.',
    all: 'Todas',
    search: 'Procurar uma palavra',
    empty: 'Ainda sem palavras.',
  },
  friends: {
    title: 'Amigos',
    inbox: 'Desafios recebidos',
    inboxNote: 'Abra a ligação que o seu amigo lhe enviou para jogar.',
    invite: 'Convidar um amigo',
    inviteNote: 'Ligação válida 7 dias. Sem conversa livre: só desafios e reações predefinidas.',
    none: 'Ainda sem amigos.',
    challenge: 'Desafiar',
    remove: 'Remover',
    ownInvite: 'É o seu próprio convite.',
    inviteInvalid: 'Convite inválido ou expirado.',
  },
  team: {
    title: 'Equipa',
    intro:
      'Junte-se ou crie uma equipa (máx. 20 membros) e alcancem juntos um objetivo semanal de palavras. Recompensas apenas estéticas.',
    name: 'Nome da equipa',
    create: 'Criar equipa',
    weekly: 'Objetivo da semana {{week}}',
    progress: '{{words}} / {{goal}} palavras',
    rewardNote: 'Objetivo alcançado: moldura «Equipa» para cada membro.',
    members: 'Membros ({{n}})',
    leave: 'Sair da equipa',
    accountRequired: 'É necessária uma conta guardada.',
    joinFailed: 'Não foi possível entrar nesta equipa.',
  },
  moderation: {
    block: 'Bloquear este jogador',
    blocked: 'Jogador bloqueado.',
    report: 'Assinalar…',
    reported: 'Assinalamento enviado.',
    reasons: {
      nickname: 'Nome inadequado',
      cheating: 'Suspeita de batota',
      harassment: 'Assédio',
      other: 'Outro',
    },
  },
  plus: {
    intro: 'O Plus apoia o jogo e acrescenta ferramentas, sem nunca mexer na pontuação:',
    fairness:
      'O núcleo do jogo, as seis línguas, o treino, os desafios e a competição pública continuam gratuitos. Nenhuma definição de dificuldade depende da assinatura.',
    features: {
      advanced_stats: 'Estatísticas avançadas',
      challenge_archives: 'Arquivo completo dos desafios terminados',
      personal_training: 'Treino personalizado determinístico',
      customisation: 'Temas e avatares exclusivos',
      private_tournaments: 'Ferramentas de torneios privados',
    },
    monthly: 'Assinar — {{price}}',
    yearly: 'Plano anual — {{price}}',
    unavailable: 'O Plus ainda não está disponível para compra.',
    testMode: 'Modo de teste: nenhum pagamento real.',
    confirming: 'A confirmar o pagamento… (ativado apenas após confirmação do fornecedor)',
    cancelled: 'Pagamento cancelado.',
    active: 'Plus ativo',
    until: 'até {{date}}',
    manage: 'Gerir a minha assinatura',
    stats: 'Estatísticas',
    mode: 'Modo',
    rounds: 'Rondas',
    avgScore: 'Pontuação média',
    contractRate: 'Contratos cumpridos',
    secPerWord: 's / palavra',
    train: 'Treino personalizado ({{lang}})',
    archive: 'Arquivo de desafios terminados',
    archiveEmpty: 'Ainda não há partidas terminadas.',
    archiveMore: 'Mostrar mais',
    archiveBot: 'robô',
    pricesNote:
      'Preços indicativos sujeitos a alteração; o montante exato é mostrado na página de pagamento.',
    notOffered: 'Esta versão não oferece nenhuma assinatura: todo o jogo é gratuito.',
  },
  legal: {
    draftBanner: 'Rascunho — a completar com a identidade real do editor.',
    privacy: 'Privacidade',
    terms: 'Condições',
    notice: 'Informações legais',
  },
  offline: {
    title: 'Jogar offline',
    explain:
      'Descarregue os dicionários para jogar sozinho e treinar sem ligação. As partidas offline não entram em nenhuma classificação.',
    download: 'Descarregar',
    ready: 'Disponível',
  },
  play: { noGame: 'Nenhuma partida em curso.' },
  install: {
    body: 'Instale o jogo no ecrã inicial para jogar mais depressa, mesmo offline no modo solo.',
    cta: 'Instalar',
    later: 'Mais tarde',
  },
  pwa: {
    updateReady: 'Está pronta uma nova versão.',
    reload: 'Atualizar',
    offline:
      'Offline: o modo solo e o treino continuam disponíveis com os dicionários já descarregados.',
  },
  settings: {
    title: 'Definições',
    uiLanguage: 'Língua da interface',
    uiLanguageNote:
      'A língua da interface é independente das línguas permitidas nas suas partidas.',
    sound: 'Sons',
    haptics: 'Vibração (se suportada)',
    motion: 'Animações',
    motionSystem: 'Conforme o sistema',
    motionReduced: 'Reduzidas',
    motionFull: 'Normais',
    relaxHints: 'Dicas de validade no modo Descontraído',
  },
  rules: {
    title: 'Regras do jogo',
    goal: {
      title: 'Objetivo',
      body: [
        'Forme palavras com as 9 letras do sorteio tocando-lhes pela ordem que quiser, sem necessidade de estarem juntas.',
        'Cada peça só se usa uma vez por palavra; todas voltam a ficar disponíveis para a palavra seguinte. Baralhar não muda o sorteio.',
        'Mínimo de três letras. A mesma palavra só pontua uma vez por ronda.',
      ],
    },
    tiles: {
      title: 'Letras e acentos',
      body: [
        'Os acentos e a cedilha não têm peça própria: «ação» joga-se A-C-A-O e aparece com a grafia correta.',
        'Ñ (espanhol) e Ä, Ö, Ü (alemão) são peças distintas, utilizáveis apenas na sua língua. ß escreve-se com dois S; œ e æ usam duas peças.',
      ],
    },
    languages: {
      title: 'Línguas e atribuição',
      body: [
        'Uma palavra válida em várias das suas línguas é atribuída a uma só. Escolha a língua antes de validar (botões Língua) ou deixe «Auto».',
        'Auto escolhe a língua válida que mais precisa de palavras para o seu contrato e, depois, a ordem das suas línguas. Uma vez aceite, a palavra não muda de língua.',
        'Nomes próprios e abreviaturas estão excluídos. Algumas palavras ofensivas são excluídas pela política de conteúdos. Poderá assinalar uma palavra recusada.',
      ],
    },
    points: {
      title: 'Pontos',
      body: [
        'Uma palavra de n letras (peças) vale 10 × (n − 2): 3 letras = 10, 4 = 20 … 9 = 70.',
        'Pontuação final = pontos base × (1 + bónus de língua + bónus de velocidade). Os bónus somam-se e o total é arredondado uma única vez à centésima.',
        'Uma palavra recusada não vale nada e não conta para o objetivo.',
      ],
    },
    contracts: {
      title: 'Contratos (Duelo tático, Sprint livre)',
      body: [
        'Especialista (1 língua): +10 %. Duo (2 línguas): +20 % com pelo menos 3 palavras em cada. Trio (3 línguas): +30 % com pelo menos 3 palavras em cada.',
        'Contrato falhado: mantém os pontos base, sem bónus de língua e sem recuo automático para um contrato inferior.',
        'Poliglota comum: +20 % (2 línguas) ou +30 % (3 línguas) com pelo menos 3 palavras por língua, igual para todos.',
      ],
    },
    speed: {
      title: 'Bónus de velocidade (Duelo tático)',
      body: [
        'Se chegar às 10 palavras válidas antes de 120 s: bónus = 20 % × (120 − t) / 120, sendo t o tempo oficial medido pelo servidor. Aos 90 s: +5 %; aos 60 s: +10 %; aos 30 s: +15 %.',
        'A 10.ª palavra aceite termina de imediato a sua ronda. O jogo avisa se o contrato não estiver cumprido.',
      ],
    },
    modes: {
      title: 'Modos',
      body: [
        'Clássico, Poliglota comum, Duelo tático, Sprint livre, Descontraído (sem cronómetro nem classificação) e treino contra robôs.',
        'Empate exato: o encontro é declarado empate, sem desempate oculto pela velocidade.',
      ],
    },
    fairness: {
      title: 'Equidade',
      body: [
        'O mesmo sorteio não tem a mesma dificuldade em todas as línguas. Cada sorteio é verificado para oferecer palavras comuns suficientes a cada configuração, sem garantir uma equidade perfeita.',
        'Os parâmetros são hipóteses em teste; uma partida mantém sempre a versão de regras e dicionários com que começou.',
      ],
    },
    robots: {
      title: 'Robôs',
      body: [
        'Os robôs são programas determinísticos (sem IA generativa) identificados como tal. Só jogam palavras válidas e nunca entram nas classificações humanas.',
      ],
    },
    keyboard: {
      title: 'Teclado',
      body: 'Escreva as letras (ou 1–9 para a posição de uma peça), Enter para validar, Retrocesso para apagar, Esc para limpar, Espaço para baralhar.',
    },
  },
  about: {
    title: 'Sobre',
    disclaimer:
      'Um jogo de letras pelo prazer de jogar em várias línguas. Não tem eficácia pedagógica demonstrada.',
    dictionaries: 'Dicionários e dados',
    frequencies: 'Frequência das palavras',
    policy: 'Política de conteúdos',
    adapted: 'lista adaptada',
    noticeFiles:
      'Os avisos completos (fontes, versões, licenças) acompanham cada dicionário descarregado.',
    noDefinitions:
      'Não são mostradas definições nem traduções enquanto não for integrada uma fonte com licença compatível.',
  },
};

export default pt;
